import { useMemo, useRef, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import ReportMonthSelect from '@/components/ReportMonthSelect'
import SearchableSelect, { type SelectOption } from '@/components/SearchableSelect'
import { monthKeyFromT } from './engine/calendar'
import { allowedDrivers } from './engine/schema'
import type { CaseInputs, OneTimeItem, SchemaEntry, View } from './engine/types'
import { formatCurrency } from './projectionsFormat'

const LOCATION_VIEWS: View[] = ['river_oaks', 'highland_park', 'ga']

const ONE_TIME_MODE_OPTIONS: SelectOption[] = [
  { id: 'incremental', label: 'Incremental' },
  { id: 'already_in_driver', label: 'Already assumed in driver' },
]

type OneTimeItemsProps = {
  schema: SchemaEntry[]
  draft: CaseInputs
  lastClosedMonth: string
  canEdit: boolean
  onAdd: (item: OneTimeItem) => void
  onUpdate: (id: string, patch: Partial<Omit<OneTimeItem, 'id'>>) => void
  onRemove: (id: string) => void
}

function projectedMonthOptions(lastClosedMonth: string): SelectOption[] {
  const options: SelectOption[] = []
  for (let t = 1; t <= 36; t += 1) {
    const id = monthKeyFromT(lastClosedMonth, t)
    options.push({ id, label: id })
  }
  return options
}

function lineHasPicker(schema: SchemaEntry[], lineKey: string, draft: CaseInputs): boolean {
  const entry = schema.find((row) => row.key === lineKey)
  if (!entry || entry.kind !== 'input') {
    return false
  }
  if (entry.bs_only) {
    return allowedDrivers(schema, lineKey, 'consolidated', draft.consolidated_only).length > 0
  }
  for (const view of LOCATION_VIEWS) {
    if (allowedDrivers(schema, lineKey, view, draft.consolidated_only).length > 0) {
      return true
    }
  }
  return allowedDrivers(schema, lineKey, 'consolidated', draft.consolidated_only).length > 0
}

function pickerLineOptions(schema: SchemaEntry[], draft: CaseInputs): SelectOption[] {
  return schema
    .filter(
      (entry) =>
        entry.kind === 'input' &&
        entry.key !== 'is.boh' &&
        entry.key !== 'is.foh' &&
        lineHasPicker(schema, entry.key, draft),
    )
    .map((entry) => ({ id: entry.key, label: entry.label }))
}

function viewsForLine(
  schema: SchemaEntry[],
  lineKey: string | null,
  consolidatedOnly: string[],
): View[] {
  if (!lineKey) {
    return []
  }
  const entry = schema.find((row) => row.key === lineKey)
  if (!entry) {
    return []
  }
  if (entry.bs_only || consolidatedOnly.includes(lineKey)) {
    return ['consolidated']
  }
  return [...LOCATION_VIEWS, 'consolidated']
}

function viewOptions(views: View[]): SelectOption[] {
  const labels: Record<View, string> = {
    river_oaks: 'River Oaks',
    highland_park: 'Highland Park',
    ga: 'G&A',
    consolidated: 'Consolidated',
  }
  return views.map((view) => ({ id: view, label: labels[view] }))
}

function newEmptyItem(): OneTimeItem {
  return {
    id: crypto.randomUUID(),
    month: '',
    view: 'river_oaks',
    line_key: '',
    amount: 0,
    mode: 'incremental',
    notes: '',
  }
}

function OneTimeRow({
  item,
  schema,
  draft,
  monthOptions,
  lineOptions,
  canEdit,
  onUpdate,
  onRemove,
}: {
  item: OneTimeItem
  schema: SchemaEntry[]
  draft: CaseInputs
  monthOptions: SelectOption[]
  lineOptions: SelectOption[]
  canEdit: boolean
  onUpdate: (id: string, patch: Partial<Omit<OneTimeItem, 'id'>>) => void
  onRemove: (id: string) => void
}) {
  const views = viewsForLine(schema, item.line_key || null, draft.consolidated_only)
  const validView = views.includes(item.view) ? item.view : (views[0] ?? 'consolidated')

  return (
    <tr key={item.id} className="border-t border-gray-100">
      <td className="!px-2 !py-2 align-top">
        <ReportMonthSelect
          id={`one-time-month-${item.id}`}
          options={monthOptions}
          value={item.month || null}
          disabled={!canEdit}
          onChange={(month) => onUpdate(item.id, { month: month ?? '' })}
        />
      </td>
      <td className="!px-2 !py-2 align-top">
        <SearchableSelect
          id={`one-time-location-${item.id}`}
          options={viewOptions(views.length ? views : ['consolidated'])}
          value={validView}
          disabled={!canEdit || !item.line_key}
          onChange={(view) => onUpdate(item.id, { view: view as View })}
        />
      </td>
      <td className="!px-2 !py-2 align-top">
        <SearchableSelect
          id={`one-time-line-${item.id}`}
          options={lineOptions}
          value={item.line_key || null}
          disabled={!canEdit}
          onChange={(lineKey) => {
            const nextViews = viewsForLine(schema, lineKey, draft.consolidated_only)
            const nextView = nextViews.includes(item.view)
              ? item.view
              : (nextViews[0] ?? 'consolidated')
            onUpdate(item.id, { line_key: lineKey ?? '', view: nextView })
          }}
        />
      </td>
      <td className="!px-2 !py-2 align-top">
        <OneTimeAmountInput
          item={item}
          canEdit={canEdit}
          onUpdate={onUpdate}
        />
      </td>
      <td className="!px-2 !py-2 align-top">
        <SearchableSelect
          id={`one-time-mode-${item.id}`}
          options={ONE_TIME_MODE_OPTIONS}
          value={item.mode}
          disabled={!canEdit}
          preserveOrder
          size="compact"
          onChange={(mode) => {
            if (mode === 'incremental' || mode === 'already_in_driver') {
              onUpdate(item.id, { mode })
            }
          }}
        />
      </td>
      <td className="!px-2 !py-2 align-top">
        <OneTimeNotesInput item={item} canEdit={canEdit} onUpdate={onUpdate} />
      </td>
      <td className="!px-2 !py-2 align-top">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          iconOnly
          disabled={!canEdit}
          aria-label="Remove row"
          onClick={() => onRemove(item.id)}
        >
          <XMarkIcon className="h-4 w-4" aria-hidden="true" />
        </Button>
      </td>
    </tr>
  )
}

function parseMoneyInput(value: string): number | null {
  const trimmed = value.trim()
  if (!trimmed || trimmed === '-') return null
  const negative = trimmed.startsWith('(') && trimmed.endsWith(')')
  const cleaned = trimmed.replace(/[$,()]/g, '').trim()
  if (!cleaned) return null
  const parsed = Number(cleaned)
  if (!Number.isFinite(parsed)) return null
  return negative ? -parsed : parsed
}

function OneTimeAmountInput({
  item,
  canEdit,
  onUpdate,
}: {
  item: OneTimeItem
  canEdit: boolean
  onUpdate: (id: string, patch: Partial<Omit<OneTimeItem, 'id'>>) => void
}) {
  const [editing, setEditing] = useState(false)
  const [amountDraft, setAmountDraft] = useState('')
  const editingRef = useRef(false)

  return (
    <input
      className="input box-border h-8 w-full min-w-[120px] px-2 text-right text-sm tabular-nums"
      value={editing ? amountDraft : formatCurrency(item.amount)}
      disabled={!canEdit}
      onFocus={() => {
        if (!canEdit) return
        editingRef.current = true
        setAmountDraft(item.amount ? String(item.amount) : '')
        setEditing(true)
      }}
      onChange={(e) => {
        if (editingRef.current) setAmountDraft(e.target.value)
      }}
      onBlur={(event) => {
        if (!editingRef.current) return
        editingRef.current = false
        const parsed = parseMoneyInput(event.currentTarget.value)
        onUpdate(item.id, { amount: parsed ?? 0 })
        setEditing(false)
      }}
    />
  )
}

function OneTimeNotesInput({
  item,
  canEdit,
  onUpdate,
}: {
  item: OneTimeItem
  canEdit: boolean
  onUpdate: (id: string, patch: Partial<Omit<OneTimeItem, 'id'>>) => void
}) {
  const [notesDraft, setNotesDraft] = useState(item.notes ?? '')

  return (
    <input
      className="input box-border h-8 w-full min-w-[160px] px-2 text-sm"
      value={notesDraft}
      disabled={!canEdit}
      onChange={(e) => setNotesDraft(e.target.value)}
      onBlur={(event) => onUpdate(item.id, { notes: event.currentTarget.value })}
    />
  )
}

export default function OneTimeItems({
  schema,
  draft,
  lastClosedMonth,
  canEdit,
  onAdd,
  onUpdate,
  onRemove,
}: OneTimeItemsProps) {
  const monthOptions = useMemo(
    () => projectedMonthOptions(lastClosedMonth),
    [lastClosedMonth],
  )
  const lineOptions = useMemo(() => pickerLineOptions(schema, draft), [schema, draft])

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="min-h-0 flex-1 overflow-auto">
        <table className="table-forecast w-full min-w-[960px]">
          <thead>
            <tr className="text-left text-xs text-gray-600">
              <th className="!px-2 !py-2">Month</th>
              <th className="!px-2 !py-2">Location</th>
              <th className="!px-2 !py-2">Line</th>
              <th className="!px-2 !py-2 !text-right">Amount</th>
              <th className="!px-2 !py-2">Mode</th>
              <th className="!px-2 !py-2">Notes</th>
              <th className="!px-2 !py-2" />
            </tr>
          </thead>
          <tbody>
            {draft.one_time_items.map((item) => (
              <OneTimeRow
                key={item.id}
                item={item}
                schema={schema}
                draft={draft}
                monthOptions={monthOptions}
                lineOptions={lineOptions}
                canEdit={canEdit}
                onUpdate={onUpdate}
                onRemove={onRemove}
              />
            ))}
          </tbody>
        </table>
        <div className="px-2 pt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!canEdit}
            onClick={() => onAdd(newEmptyItem())}
          >
            Add row
          </Button>
        </div>
      </div>
    </div>
  )
}
